import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useTranslation } from "react-i18next"

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@f2e/ui/components/base"

import {
  AuthLinkResultCard,
  PasswordBasicErrorMessage,
  PasswordRequirements,
} from "@/components/Auth"
import { API_PREFIX } from "@/constants"
import { ErrorCode } from "@/constants/errors"
import { useSysConfig, useToast } from "@/hooks"
import {
  InvitationStatusResponse,
  useInvitationAcceptPasswordApi,
  useInvitationStatusApi,
} from "@/hooks/api/account"
import type { ApiErrorWithData } from "@/lib/authErrors"
import { getAuthPasswordErrorMessage } from "@/lib/authErrors"
import { getNewPasswordFormError } from "@/lib/passwordValidation"

const AcceptInvitation = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") || ""
  const config = useSysConfig()
  const { alertToast } = useToast()
  const { fetchFn: fetchInvitationStatus } = useInvitationStatusApi(token)
  const { fetchFn: acceptPassword } = useInvitationAcceptPasswordApi(token)
  const [status, setStatus] = useState<InvitationStatusResponse | null>(null)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [name, setName] = useState("")
  const [loadError, setLoadError] = useState("")
  const [canRetryLoad, setCanRetryLoad] = useState(false)
  const isExistingGoogleOnly =
    Boolean(status?.existing_user) && !status?.existing_user_has_password
  const isSettingPassword =
    Boolean(status) && (!status?.existing_user || isExistingGoogleOnly)
  const goToLogin = () => navigate("/login")

  const getInvitationLinkErrorResult = useCallback(
    (error: unknown) => {
      const apiError = error as ApiErrorWithData
      const errorCode = apiError.data?.error_code
      if (
        errorCode === ErrorCode.INVITATION_INVALID ||
        errorCode === ErrorCode.INVITATION_EXPIRED ||
        errorCode === ErrorCode.INVITATION_NO_LONGER_VALID ||
        errorCode === ErrorCode.INVITATION_EMAIL_MISMATCH
      ) {
        return {
          message: t(`common.auth.login.errorCodes.${errorCode}`),
          canRetry: false,
        }
      }
      return {
        message:
          apiError.response?.status === 429
            ? t("common.auth.errors.tooManyRequests")
            : t("common.auth.invitation.errors.loadFailed"),
        canRetry: true,
      }
    },
    [t]
  )

  const loadInvitationStatus = useCallback(() => {
    setStatus(null)
    setLoadError("")
    setCanRetryLoad(false)
    if (!token) {
      setLoadError(t("common.auth.invitation.errors.missingInfo"))
      return
    }
    void fetchInvitationStatus({
      callbackFn: (resp) => {
        if (resp?.data) {
          setStatus(resp.data)
        }
      },
    }).catch((error) => {
      const result = getInvitationLinkErrorResult(error)
      setLoadError(result.message)
      setCanRetryLoad(result.canRetry)
    })
  }, [fetchInvitationStatus, getInvitationLinkErrorResult, token, t])

  useEffect(() => {
    loadInvitationStatus()
  }, [loadInvitationStatus])

  const handleGoogle = () => {
    window.location.href = `${config.serverRoot}${API_PREFIX}/accounts/invitations/${token}/redeem/google`
  }

  const handlePassword = async () => {
    if (!status) {
      alertToast({
        title: t("common.auth.invitation.errors.title"),
        description: t("common.auth.invitation.errors.passwordRequired"),
      })
      return
    }

    if (!isSettingPassword && !password) {
      alertToast({
        title: t("common.auth.invitation.errors.title"),
        description: t("common.auth.invitation.errors.passwordRequired"),
      })
      return
    }

    const passwordError = isSettingPassword
      ? getNewPasswordFormError({ password, confirmPassword })
      : null
    if (passwordError) {
      const errorMessages = {
        passwordRequired: t("common.auth.invitation.errors.passwordRequired"),
        confirmPasswordRequired: t(
          "common.auth.invitation.errors.confirmPasswordRequired"
        ),
        passwordsDoNotMatch: t("common.auth.errors.passwordsDoNotMatch"),
        passwordPolicy: t("common.auth.errors.passwordPolicy"),
      }
      alertToast({
        title: t("common.auth.invitation.errors.title"),
        description: errorMessages[passwordError],
      })
      return
    }
    try {
      const resp = await acceptPassword({
        jsonBody: {
          email: status.email,
          password,
          name: name || undefined,
        },
      })
      if (resp?.data) {
        localStorage.setItem("loggedIn", JSON.stringify({ type: "email" }))
        navigate("/")
        return
      }
      throw new Error(t("common.auth.invitation.errors.acceptFailed"))
    } catch (error) {
      const message =
        getAuthPasswordErrorMessage(
          error,
          t,
          "common.auth.invitation.errors.acceptFailed"
        ) ||
        (error as Error).message ||
        t("common.auth.invitation.errors.acceptFailed")
      alertToast({
        title: t("common.auth.invitation.errors.title"),
        description: message,
      })
    }
  }

  if (loadError || !status) {
    return (
      <div className="container flex min-h-[600px] items-center justify-center">
        <AuthLinkResultCard
          title={t("common.auth.invitation.title")}
          description={loadError || t("common.auth.invitation.loading")}
          variant={loadError ? "error" : "loading"}
          primaryAction={
            loadError
              ? {
                  label: canRetryLoad
                    ? t("common.auth.actions.retry")
                    : t("common.auth.actions.backToLogin"),
                  onClick: canRetryLoad ? loadInvitationStatus : goToLogin,
                }
              : undefined
          }
        />
      </div>
    )
  }

  return (
    <div className="container flex min-h-[600px] items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {t("common.auth.invitation.title")}
          </CardTitle>
          <CardDescription>
            {t("common.auth.invitation.description", {
              organization: status.organization_name,
              workspace: status.workspace_name,
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="rounded-md border border-slate-200 px-3 py-2 text-sm">
              {status.email}
            </div>
            <Button className="w-full" onClick={handleGoogle}>
              {t("common.auth.invitation.continueGoogle")}
            </Button>
            <div className="flex items-center gap-3 text-xs font-medium text-slate-500">
              <div className="h-px flex-1 bg-slate-200" />
              <span>{t("common.auth.invitation.or")}</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            {isExistingGoogleOnly && (
              <p className="text-sm text-slate-500">
                {t("common.auth.invitation.setPasswordForExisting")}
              </p>
            )}
            {!status.existing_user && (
              <div className="grid gap-2">
                <Label htmlFor="name">{t("common.auth.fields.name")}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
            )}
            <div className="grid gap-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="password">
                  {status.existing_user
                    ? status.existing_user_has_password
                      ? t("common.auth.fields.password")
                      : t("common.auth.fields.setPassword")
                    : t("common.auth.fields.createPassword")}
                </Label>
                {isSettingPassword && <PasswordRequirements />}
              </div>
              <Input
                id="password"
                type="password"
                minLength={isSettingPassword ? 8 : undefined}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {isSettingPassword && (
                <PasswordBasicErrorMessage
                  password={password}
                  show={password.length > 0}
                />
              )}
            </div>
            {isSettingPassword && (
              <div className="grid gap-2">
                <Label htmlFor="confirm-password">
                  {t("common.auth.fields.confirmPassword")}
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </div>
            )}
            <Button className="w-full" onClick={handlePassword}>
              {isExistingGoogleOnly
                ? t("common.auth.invitation.setPasswordAndContinue")
                : t("common.auth.invitation.continueEmail")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default AcceptInvitation
